import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/UniversityCard.css";
import { getUniversityCategory } from "../utils/getUniversityCategory";
import { createSlug } from "../utils/pagination";
// Import university image
import harvardImg from "../assets/universities/hardvard.jpg";
import { useAuth } from "../context/AuthContext";

const UniversityCard = ({ university, category }) => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  // State to track image loading errors
  const [imageError, setImageError] = useState(false);

  // Use Harvard image for all universities as requested
  const getUniversityImage = () => {
    return harvardImg;
  };

  // Handle image loading errors
  const handleImageError = () => {
    setImageError(true);
  };

  // Handle login button click
  const handleLoginClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/login");
  };

  // Use provided category or calculate it
  const universityCategory =
    category || getUniversityCategory(university.tuitionFees);

  // Format tuition fee with comma separators and dollar sign
  const formatCurrency = (amount) => {
    return amount
      ? `$${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
      : "N/A";
  };

  return (
    <Link
      to={`/university/${createSlug(university.name)}`}
      className="university-card-link"
    >
      <div className="university-card">
        <div className="university-card-header">
          {/* Only show category badge if tuition fees are available */}
          {university.tuitionFees && university.tuitionFees !== "N/A" ? (
            <span
              className={`category-badge ${universityCategory.toLowerCase()}`}
            >
              {universityCategory}
            </span>
          ) : (
            <span className="category-badge-placeholder"></span>
          )}
          {university.country && (
            <span className="country-flag">
              {(() => {
                const country = university.country.toLowerCase();
                if (
                  [
                    "usa",
                    "us",
                    "united states",
                    "united states of america",
                  ].includes(country)
                ) {
                  return "🇺🇸";
                } else if (country === "canada") {
                  return "🇨🇦";
                } else {
                  return "🌎";
                }
              })()}
            </span>
          )}
        </div>

        <div className="university-image">
          <img
            src={getUniversityImage()}
            alt={university.name}
            onError={handleImageError}
            style={
              imageError ? { opacity: 0.5, backgroundColor: "#f0f0f0" } : {}
            }
          />
        </div>

        <div className="university-content">
          <h3 className="university-name">{university.name}</h3>

          <div className="university-stats">
            {/* Only show tuition if it has a valid value */}
            {university.tuitionFees && university.tuitionFees !== "N/A" && (
              <div className="stat-item">
                <span className="stat-label">Tuition</span>
                <span className="stat-value">
                  {formatCurrency(university.tuitionFees)}
                </span>
              </div>
            )}
            {/* Only show living cost if it has a valid value */}
            {university.livingExpenses &&
              university.livingExpenses !== "N/A" && (
                <div className="stat-item">
                  <span className="stat-label">Living Cost</span>
                  <span className="stat-value">
                    {formatCurrency(university.livingExpenses)}
                  </span>
                </div>
              )}
            {/* Do not display budget living expenses here */}
            {/* Show acceptance rate for logged in users, login prompt for others */}
            {isLoggedIn ? (
              <div className="stat-item">
                <span className="stat-label">Acceptance</span>
                <span className="stat-value">{university.acceptanceRate}%</span>
              </div>
            ) : (
              <div className="stat-item">
                <span className="stat-label">Acceptance</span>
                <button className="login-btn-small" onClick={handleLoginClick}>
                  Login to view
                </button>
              </div>
            )}
          </div>

          {/* Only show requirements for logged in users */}
          {isLoggedIn ? (
            <div className="university-requirements">
              {university.averageGRE && (
                <div className="requirement-item">
                  <span className="requirement-name">GRE</span>
                  <span className="requirement-score">
                    {university.averageGRE}
                  </span>
                </div>
              )}
              {/* Only show GMAT for non-USA universities */}
              {university.averageGMAT && university.country !== "USA" && (
                <div className="requirement-item">
                  <span className="requirement-name">GMAT</span>
                  <span className="requirement-score">
                    {university.averageGMAT}
                  </span>
                </div>
              )}
              {university.averageToefl && (
                <div className="requirement-item">
                  <span className="requirement-name">TOEFL</span>
                  <span className="requirement-score">
                    {university.averageToefl}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="university-requirements">
              <button
                className="login-btn-requirements"
                onClick={handleLoginClick}
              >
                Login to view requirements
              </button>
            </div>
          )}

          <div className="card-actions">
            <div className="know-more-btn">View Details</div>
          </div>
          {/* Show a message for more details if not logged in */}
          {!isLoggedIn && (
            <div className="login-for-more-details">
              <span>Login to view more details about this university.</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default UniversityCard;
